import {
  createRecipe,
  createSuggestedActivity,
  deleteRecipe,
  deleteSuggestedActivity,
  fetchRecipes,
  fetchSuggestedActivities,
  fetchSuggestedActivitiesByCategory,
  loadRecipe,
  loadSuggestedActivity,
  selectRecipeForToday as serviceSelectRecipeForToday,
  selectSuggestedActivityForToday as serviceSelectSuggestedActivityForToday,
  updateRecipe,
  updateSuggestedActivity,
} from '@core/services/suggested-activities/suggested-activities-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { Recipe, SuggestedActivity } from '@domain/models/suggested-activities'

function dedupeCategoryIds(categoryIds: string[]): string[] {
  return [...new Set(categoryIds.filter((item) => item.trim().length > 0))]
}

export class SuggestedActivitiesFeature {
  public static async getSuggestedActivities(): Promise<SuggestedActivity[]> {
    try {
      return await fetchSuggestedActivities()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch suggested activities.',
        underlyingException: error,
      })
    }
  }

  public static async getRecipes(): Promise<Recipe[]> {
    try {
      return await fetchRecipes()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch suggested recipes.',
        underlyingException: error,
      })
    }
  }

  public static async getSuggestedActivitiesByCategory(
    categoryId: string,
  ): Promise<SuggestedActivity[]> {
    try {
      return await fetchSuggestedActivitiesByCategory(categoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch suggested activities for category "${categoryId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getSuggestedActivity(
    uid: string,
  ): Promise<SuggestedActivity | null> {
    try {
      return await loadSuggestedActivity(uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to load suggested activity "${uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async getRecipe(uid: string): Promise<Recipe | null> {
    try {
      return await loadRecipe(uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to load recipe "${uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async selectSuggestedActivityForToday(
    categoryId: string,
  ): Promise<SuggestedActivity | null> {
    try {
      return await serviceSelectSuggestedActivityForToday(categoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to select suggested activity for category "${categoryId}".`,
        underlyingException: error,
      })
    }
  }

  public static async selectSuggestedActivityForTodayByCategoryIds(
    categoryIds: string[],
  ): Promise<SuggestedActivity | null> {
    const uniqueIds = dedupeCategoryIds(categoryIds)
    for (const categoryId of uniqueIds) {
      const selected = await SuggestedActivitiesFeature.selectSuggestedActivityForToday(
        categoryId,
      )
      if (selected) {
        return selected
      }
    }
    return null
  }

  public static async selectRecipeForToday(): Promise<Recipe | null> {
    try {
      return await serviceSelectRecipeForToday()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to select recipe for today.',
        underlyingException: error,
      })
    }
  }

  public static async createSuggestedActivity(
    activity: Omit<SuggestedActivity, 'uid'>,
  ): Promise<string> {
    try {
      return await createSuggestedActivity(activity)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create suggested activity.',
        underlyingException: error,
      })
    }
  }

  public static async updateSuggestedActivity(
    uid: string,
    activity: Omit<SuggestedActivity, 'uid'>,
  ): Promise<void> {
    try {
      await updateSuggestedActivity(uid, activity)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update suggested activity "${uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteSuggestedActivity(uid: string): Promise<void> {
    try {
      await deleteSuggestedActivity(uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete suggested activity "${uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async createRecipe(recipeInput: Omit<Recipe, 'uid'>): Promise<string> {
    try {
      return await createRecipe(recipeInput)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create recipe.',
        underlyingException: error,
      })
    }
  }

  public static async updateRecipe(
    uid: string,
    recipeInput: Omit<Recipe, 'uid'>,
  ): Promise<void> {
    try {
      await updateRecipe(uid, recipeInput)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update recipe "${uid}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteRecipe(uid: string): Promise<void> {
    try {
      await deleteRecipe(uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete recipe "${uid}".`,
        underlyingException: error,
      })
    }
  }
}

export async function getSuggestedActivities(): Promise<SuggestedActivity[]> {
  return SuggestedActivitiesFeature.getSuggestedActivities()
}

export async function getRecipes(): Promise<Recipe[]> {
  return SuggestedActivitiesFeature.getRecipes()
}

export async function getSuggestedActivitiesByCategory(
  categoryId: string,
): Promise<SuggestedActivity[]> {
  return SuggestedActivitiesFeature.getSuggestedActivitiesByCategory(categoryId)
}

export async function getSuggestedActivity(
  uid: string,
): Promise<SuggestedActivity | null> {
  return SuggestedActivitiesFeature.getSuggestedActivity(uid)
}

export async function getRecipe(uid: string): Promise<Recipe | null> {
  return SuggestedActivitiesFeature.getRecipe(uid)
}

export async function selectSuggestedActivityForTodayByCategoryIds(
  categoryIds: string[],
): Promise<SuggestedActivity | null> {
  return SuggestedActivitiesFeature.selectSuggestedActivityForTodayByCategoryIds(categoryIds)
}

export async function selectSuggestedActivityToday(
  categoryId: string,
): Promise<SuggestedActivity | null> {
  return SuggestedActivitiesFeature.selectSuggestedActivityForToday(categoryId)
}

export async function selectSuggestedActivityForToday(
  categoryId: string,
): Promise<SuggestedActivity | null> {
  return SuggestedActivitiesFeature.selectSuggestedActivityForToday(categoryId)
}

export async function selectRecipeForToday(): Promise<Recipe | null> {
  return SuggestedActivitiesFeature.selectRecipeForToday()
}

export async function createSuggestedActivityItem(
  activity: Omit<SuggestedActivity, 'uid'>,
): Promise<string> {
  return SuggestedActivitiesFeature.createSuggestedActivity(activity)
}

export async function updateSuggestedActivityItem(
  uid: string,
  activity: Omit<SuggestedActivity, 'uid'>,
): Promise<void> {
  return SuggestedActivitiesFeature.updateSuggestedActivity(uid, activity)
}

export async function deleteSuggestedActivityItem(uid: string): Promise<void> {
  return SuggestedActivitiesFeature.deleteSuggestedActivity(uid)
}

export async function createRecipeItem(
  recipeInput: Omit<Recipe, 'uid'>,
): Promise<string> {
  return SuggestedActivitiesFeature.createRecipe(recipeInput)
}

export async function updateRecipeItem(
  uid: string,
  recipeInput: Omit<Recipe, 'uid'>,
): Promise<void> {
  return SuggestedActivitiesFeature.updateRecipe(uid, recipeInput)
}

export async function deleteRecipeItem(uid: string): Promise<void> {
  return SuggestedActivitiesFeature.deleteRecipe(uid)
}
